// Shared IMMO ID decoder — used by the IMMO Calculator page and the Flash Tools EEPROM tab.
// Given an EEPROM dump (ArrayBuffer or Uint8Array) + a model definition, extract the IMMO ID.

export const toInt = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return NaN;
  return s.startsWith("0x") ? parseInt(s, 16) : parseInt(s, 10);
};

// Built-in decoders that use a custom algorithm (not a simple offset read).
export const BUILTIN_MODELS = [
  {
    id: "builtin-xadv-kline", builtin: true,
    name: "Honda X-ADV 750 K-Line (38770-MKH-D22)", brand: "Honda", eeprom: "2KB · K-Line",
    file_size: "2048", offset: "0x180", length: "8", endian: "little", format: "xadv14", pad: "9",
    notes: "Two 14-bit halves stored LE @0x180 & @0x186, each with 0x8000 marker bit. ID = ((w2&0x3FFF)<<14)|(w1&0x3FFF).",
  },
  {
    id: "builtin-scoopy-k2f", builtin: true,
    name: "Honda Scoopy K2F (30400-K2F-N91)", brand: "Honda", eeprom: "93C46 · 512B",
    file_size: "512", offset: "0x48", offsetB: "0x46", length: "8", endian: "little", format: "xadv14", pad: "9",
    notes: "Two 14-bit halves stored LE with the 0x8000 marker bit: low half (wa) @0x48, high half (wb) @0x46. ID = ((wb&0x3FFF)<<14)|(wa&0x3FFF). Verified: Immo ID 055902642. Also covers Scoopy/BeAT-class 93C46 dumps using this layout.",
  },
  {
    id: "builtin-canbus-scu", builtin: true,
    name: "Honda CANbus SCU (38770-MKx-D04)", brand: "Honda", eeprom: "3KB · SCU · CANbus",
    file_size: "3072", offset: "0x08", length: "4", endian: "big", format: "decimal", pad: "9",
    notes: "Full IMMO ID stored as 4-byte big-endian @0x08 (mirrored @0x18). Covers X-ADV & Forza 38770-MKT-D04 / MKV-D04. This SCU dump holds the COMPLETE ID including the leading digit.",
  },
  {
    id: "builtin-canbus-ecu", builtin: true,
    name: "Honda CANbus ECU (38770-MKx-D04) · low-24", brand: "Honda", eeprom: "16KB · ECU · CANbus",
    file_size: "16384", offset: "0x0e", length: "3", endian: "little", format: "canbus24", pad: "",
    notes: "ECU dump stores only the LOW 24 bits: ((b[0x0F]<<16)|(b[0x0E]<<8)|b[0x11]). The leading digit(s) are NOT stored here — read the SCU EEPROM for the full IMMO ID.",
  },
  {
    id: "builtin-vario-k59j", builtin: true,
    name: "Honda Vario K59J-A71 (30400-K59J · Shindengen 1024B)", brand: "Honda", eeprom: "1024B · Shindengen immo",
    file_size: "1024", offset: "0x98", length: "4", endian: "little", format: "decimal", pad: "9",
    notes: "Full 32-bit IMMO ID as 4-byte little-endian @0x98 = 0x0225A936 (low word 0xA936 stored ×6 from 0x8E, high word 0x0225 stored ×6 from 0x9A). Verified: Immo ID 036022582. Dump via native bridge Cold Read EEPROM 1024B (bridge v1.9.33+).",
  },
  {
    id: "builtin-pcx160-k1zg", builtin: true,
    name: "Honda PCX 160 (30400-K1ZG-N31 · 93C76 1024B)", brand: "Honda", eeprom: "93C76 · 1024B EEPROM",
    file_size: "1024", offset: "0x4A", length: "4", endian: "little", format: "decimal", pad: "9",
    notes: "Full 32-bit IMMO ID as 4-byte little-endian @0x4A = 0x079CC26C (low word 0xC26C stored ×6 from 0x40, high word 0x079C stored ×6 from 0x4C). Verified: IMMO ID 127713900. Correct dump = 1024B (93C76) — a 512B read is only the first half. Cold Read EEPROM 1024B.",
  },
];

// Normalise any input to a plain ArrayBuffer so the 3-arg Uint8Array(buffer, off, len) form works.
const toArrayBuffer = (buf) => {
  if (buf instanceof ArrayBuffer) return buf;
  if (buf && buf.buffer instanceof ArrayBuffer) return buf.buffer.slice(buf.byteOffset || 0, (buf.byteOffset || 0) + buf.byteLength);
  return buf;
};

// Extract the IMMO ID from a dump using a model definition. Returns a string or null.
export function extractImmo(input, m) {
  const buf = toArrayBuffer(input);
  if ((m.format || "") === "canbus24") {
    const u8 = new Uint8Array(buf);
    if (u8.byteLength < 0x12) return null;
    const id = (u8[0x0f] << 16) | (u8[0x0e] << 8) | u8[0x11];
    let s = id.toString();
    const pad = toInt(m.pad);
    if (!Number.isNaN(pad) && pad > 0) s = s.padStart(pad, "0");
    return s;
  }
  if ((m.format || "") === "xadv14") {
    const u8 = new Uint8Array(buf);
    const A = Number.isNaN(toInt(m.offset)) ? 0x180 : toInt(m.offset);   // low 14-bit half (wa)
    const B = Number.isNaN(toInt(m.offsetB)) ? A + 6 : toInt(m.offsetB); // high 14-bit half (wb)
    if (A + 1 >= u8.byteLength || B + 1 >= u8.byteLength || A < 0 || B < 0) return null;
    const wa = u8[A] | (u8[A + 1] << 8);
    const wb = u8[B] | (u8[B + 1] << 8);
    if (!(wa & 0x8000) || !(wb & 0x8000)) return null; // marker bits must be set
    const id = ((wb & 0x3fff) << 14) | (wa & 0x3fff);
    let s = id.toString();
    const pad = toInt(m.pad);
    if (!Number.isNaN(pad) && pad > 0) s = s.padStart(pad, "0");
    return s;
  }
  const off = toInt(m.offset);
  const len = toInt(m.length);
  if (Number.isNaN(off) || Number.isNaN(len) || off < 0 || len <= 0) return null;
  if (off + len > buf.byteLength) return null;
  let bytes = Array.from(new Uint8Array(buf, off, len));
  const little = (m.endian || "little") === "little";
  const fmt = m.format || "decimal";

  if (fmt === "ascii") {
    return bytes.map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : "")).join("");
  }
  const ordered = little ? [...bytes].reverse() : bytes; // big-endian order
  if (fmt === "hex") return ordered.map((b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
  if (fmt === "bcd") return ordered.map((b) => b.toString(16).padStart(2, "0")).join("");

  // decimal
  let val = 0n;
  for (const b of ordered) val = (val << 8n) | BigInt(b);
  let s = val.toString();
  const pad = toInt(m.pad);
  if (!Number.isNaN(pad) && pad > 0) s = s.padStart(pad, "0");
  return s;
}

// Auto-detect the best IMMO candidate from a dump. Prefers file-size matches + plausible IDs.
export function autoDetectImmo(input, extra = []) {
  const buf = toArrayBuffer(input);
  const size = buf.byteLength;
  const models = [...BUILTIN_MODELS, ...(extra || [])];
  const plausible = (s) => !!s && s.length >= 6 && /[1-9]/.test(s);
  const score = (r) => (r.sizeMatch ? 2 : 0) + (plausible(r.immo) ? 1 : 0);
  const results = models
    .map((m) => ({ model: m, immo: extractImmo(buf, m), sizeMatch: toInt(m.file_size) === size }))
    .filter((r) => r.immo)
    .sort((a, b) => score(b) - score(a));
  return results[0] || null;
}
